import React from "react";
import { Box } from "@mui/material";
import { certificatePageSize } from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { starPoints } from "./ornamentShapes";

/** The fine double rule, this far in from the page edge. */
const RULE_INSET = 20;
const RULE_GAP = 6;
/** How far each corner flourish reaches along the rules. */
const FLOURISH_REACH = 92;

/**
 * One corner's flourish, drawn for the top-left with the page corner at the
 * origin: a curl off each rule, a small leaf between them and a dot.
 */
const CornerFlourish: React.FC<{ transform: string; colour: string }> = ({
  transform,
  colour,
}) => {
  const r = RULE_INSET + RULE_GAP;
  /** A curl off the top rule, rolling in towards the corner. */
  const curl = `M${FLOURISH_REACH},${r} C${FLOURISH_REACH - 30},${r} ${r + 30},${r + 6} ${r + 30},${r + 26} C${r + 30},${r + 40} ${r + 12},${r + 40} ${r + 12},${r + 28} C${r + 12},${r + 20} ${r + 22},${r + 18} ${r + 24},${r + 24}`;
  return (
    <g transform={transform} fill="none" stroke={colour} strokeLinecap="round">
      <path d={curl} strokeWidth={2.5} />
      {/* The same curl mirrored across the diagonal, off the side rule. */}
      <path d={curl} strokeWidth={2.5} transform="matrix(0 1 1 0 0 0)" />
      <path
        d={`M${r + 34},${r + 34} q22,-4 30,-26 q-22,4 -30,26 z`}
        fill={colour}
        strokeWidth={1}
      />
      <path
        d={`M${r + 34},${r + 34} q-4,22 -26,30 q4,-22 26,-30 z`}
        fill={colour}
        strokeWidth={1}
      />
      <circle cx={r + 8} cy={r + 8} r={3.5} fill={colour} stroke="none" />
    </g>
  );
};

/**
 * A gold seal with a scalloped edge, two rings and a star, no text. It's an
 * SVG drawn to whatever height the layout leaves it, up to its full size.
 */
const Seal: React.FC<{ primary: string; accent: string }> = ({
  primary,
  accent,
}) => (
  <Box
    component="svg"
    viewBox="-60 -60 120 120"
    aria-hidden="true"
    sx={{ height: "100%", maxHeight: 112, aspectRatio: "1" }}
  >
    <polygon points={starPoints(28, 58, 52)} fill={primary} />
    <circle r={47} fill={accent} />
    <circle r={41} fill="none" stroke={primary} strokeWidth={2} />
    <circle
      r={35}
      fill="none"
      stroke={primary}
      strokeWidth={1}
      strokeDasharray="2 3"
    />
    {/* A laurel of small leaves round the star. */}
    {Array.from({ length: 14 }, (_, i) => {
      const side = i < 7 ? -1 : 1;
      const angle = 200 - (i % 7) * 22;
      return (
        <ellipse
          key={i}
          rx={3}
          ry={6.5}
          fill={primary}
          transform={`scale(${side} 1) rotate(${angle}) translate(0 -27) rotate(30)`}
        />
      );
    })}
    <polygon points={starPoints(5, 16, 7)} fill={primary} />
  </Box>
);

/**
 * Prestige: gold on black, like a professional training certificate. A fine
 * double rule with a flourish in each corner, and a gold seal above the
 * signatures. Its suggested look sets a charcoal paper, so the text is
 * printed in light ink.
 */
export const prestigeDesign: CertificateDesign = {
  bodyFont: "lora",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        lettering={{ nameColour: accent }}
        inset={{
          landscape: "64px 120px 56px",
          portrait: "100px 76px 84px",
        }}
        centrepiece={<Seal primary={primary} accent={accent} />}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            <rect
              x={RULE_INSET}
              y={RULE_INSET}
              width={width - RULE_INSET * 2}
              height={height - RULE_INSET * 2}
              fill="none"
              stroke={primary}
              strokeWidth={2}
            />
            <rect
              x={RULE_INSET + RULE_GAP}
              y={RULE_INSET + RULE_GAP}
              width={width - (RULE_INSET + RULE_GAP) * 2}
              height={height - (RULE_INSET + RULE_GAP) * 2}
              fill="none"
              stroke={primary}
              strokeOpacity={0.6}
              strokeWidth={1}
            />
            {[
              "",
              `translate(${width} 0) scale(-1 1)`,
              `translate(${width} ${height}) scale(-1 -1)`,
              `translate(0 ${height}) scale(1 -1)`,
            ].map((transform) => (
              <CornerFlourish
                key={transform}
                transform={transform}
                colour={primary}
              />
            ))}
          </Box>
        }
      />
    );
  },
};
